
import { Member } from './../../../member/model/member';
import { Dictionary } from './../../../core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { OrganizationService } from './../../../core/service/organization.service';
import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, AfterViewChecked, ElementRef, Renderer2 } from '@angular/core';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { MemberConsentListService } from 'src/app/core/service/grid-data/grid-data-member-consent.service';
import { DataStateChangeEvent, GridDataResult, SelectAllCheckboxState } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { IntlService } from '@progress/kendo-angular-intl';
import { SettingService } from 'src/app/core/service/setting.service';
import moment from 'moment';
import { Guid } from 'guid-typescript';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Organization } from 'src/app/core/model/organization';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { MemberService } from 'src/app/core/service/member.service';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { AssessmentType } from 'src/app/core/model/assessmentType';
import { MemberConsentService } from 'src/app/core/service/member-consent.service';

@Component({
  standalone: false,
  selector: 'app-patient-edit-consent',
  templateUrl: './patient-edit-consent.component.html',
  styleUrls: ['./patient-edit-consent.component.scss']
})
export class PatientEditConsentComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() public isReadOnly = false;
  @Input() public pendingConsents = [];
  @Input() public deletedConsents = [];
  @Input() public patientName = '';

  @Output() public add = new EventEmitter<any>();
  @Output() public delete = new EventEmitter<any>();

  public organizations = [];
  public consentableDataTypes = [];
  public consentTypes = [];
  public consentSignedBy = [];
  public columnsConfig: ColumnSettings[];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public pageSizes = [5, 10, 25, 50, 100];
  public consentFormGroup: any = null;
  public maxConsentDate = new Date();
  public deleteExistingConsentAllowed = false;
  public currentUserOrganization: Organization;
  public currentUserRole: string;
  public revokeAllOpened = false;
  public editing = false;
  public detailsUpdated = false;
  public link: any = [];
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    sort: [],
    filter: {
      logic: 'and',
      filters: []
    }
  };
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public selectedCaseKeys: number[] = [];
  public revokeConsentsDialogOpened = false;
  public hasConsentTypeAssessment = false;

  private currentMember = null;
  private listSubscription: Subscription;
  private consentExpiration = null;
  private consentModel = null;
  private consentableOptionsDisabledReadOnlyOnInsert: string;
  private messagingSubscription: Subscription;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private assessmentService: AssessmentService,
    private fb: UntypedFormBuilder,
    private organizationService: OrganizationService,
    private dictionaryService: DictionaryService,
    private translate: TranslateService,
    private appConfigService: AppConfigService,
    private settingService: SettingService,
    private memberConsentListService: MemberConsentListService,
    private messageHandlerService: MessageHandlerService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private messengerService: MessengerService,
    public intl: IntlService,
    private memberService: MemberService,
    private memberConsentService: MemberConsentService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.deleteExistingConsentAllowed = this.authenticationService.currentUser.role === 'ROLE_OPERATOR';
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.consentExpiration = sessionSetting.consentExpiration;
        this.consentModel = sessionSetting.consentModel;
        this.consentableOptionsDisabledReadOnlyOnInsert = sessionSetting.consentableOptionsDisabledReadOnlyOnInsert;
      }
    });

    if (this.authenticationService.currentUser)
    {
      this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.currentUserOrganization = user.organization;
          this.currentUserRole = this.authenticationService.currentUser.role;
        }
      });
    }
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.memberConsentListService.loading;
  }

  @Input() public set member(member: Member)
  {
    this.currentMember = member;

    const queries = [
      this.organizationService.getCachedOrganizations(),
      this.dictionaryService.getCachedConsentableDataTypes(),
      this.dictionaryService.getCachedConsentTypes(),
      this.dictionaryService.getCachedConsentSignedBy()
    ];

    forkJoin(queries).subscribe({
      next: (responseList: any) =>
      {
        this.organizations = responseList[0].filter((x: Organization) => x.consentCollects !== null && x.consentCollects.length > 0);
        this.consentableDataTypes = responseList[1];
        this.consentTypes = responseList[2];
        this.consentSignedBy = responseList[3];
        this.translate.get(['SOURCE-ORGANIZATION', 'CONSENT-DATE-SIGNED', 'CONSENT-EXPIRATION-DATE', 'CONSENT-EXPIRATION',
          'TYPE', 'SIGNED-BY', 'CONSENTED-ORGANIZATION', 'AUTHORIZED-BY']).subscribe({
            next: (res) =>
            {
              this.initGridColumns(res);
              this.loadMemberConsents();
            }
          });
      }
    });
  }

  public ngOnInit(): void
  {
    this.assessmentService.getCachedAssessmentTypes().subscribe({
      next: (data: AssessmentType[]) =>
      {
        // We assumed that there is an consent assessment
        this.hasConsentTypeAssessment = data.filter(x => x.active && x.consentType).length > 0;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.listSubscription = this.memberConsentListService.subscribe({
      next: (value) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.editing = false;
            if (d.signedDate !== null && d.signedDate !== undefined && d.signedDate.length === 10)
            {
              d.signedDate = new Date(`${d.signedDate}T12:00:00.000`);
            }
            if (d.expirationDate !== null && d.expirationDate !== undefined && d.expirationDate.length === 10)
            {
              d.expirationDate = new Date(`${d.expirationDate}T12:00:00.000`);
            }
            if (d.revokedDate !== null && d.revokedDate !== undefined && typeof d.revokedDate === 'string')
            {
              d.revokedDate = new Date(d.revokedDate);
            }
            d.memberConsentStatusText = this.memberConsentService.getMemberConsentStatusText(d.memberConsentStatus);
          });
        }

        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };

        // If pending records, added them at the top of the page
        this.pendingConsents.forEach((row) => { this.gridView.data.unshift(row); });
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'refreshMemberConsentTab')
        {
          this.memberConsentListService.queryByState(this.state);
        }
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.updateDetailsModal();
    if (this.detailsUpdated) 
    {
      return;
    }
    // only check for the link if the page isn't fully loaded yet
    this.getDetailsLink();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Add Consent handler
   *
   * @param event Grid Event Handler
   */
  public consentAddHandler(event: GridEventHandler): void
  {
    this.editing = true;

    // Get the default organization
    let defaultOrganization: Organization;
    if (this.currentUserOrganization !== undefined)
    {
      defaultOrganization = this.organizations.find(x => x.id === this.currentUserOrganization.id);
    }

    // Show warning if the default user organization do not have any consentable options
    if (defaultOrganization === undefined)
    {
      this.messageHandlerService.displayWarning('NO-CONSENTABLE-OPTIONS-FOR-ORGANIZATION');
    }

    const fields = {
      id: Guid.create().toString(),
      organization: [{
        value: defaultOrganization,
        disabled: this.currentUserRole !== 'ROLE_OPERATOR'
      }, Validators.required],
      signedDate: [{ value: null, disabled: false }, Validators.required],
      expirationDate: [{ value: null, disabled: false }, Validators.required],
      type: [{ value: null, disabled: false }, Validators.required],
      signedBy: [{ value: null, disabled: false }, Validators.required],
      authorizedBy: [{ value: null, disabled: true }],
      editing: true
    };

    this.consentableDataTypes.forEach((consentType: Dictionary) =>
    {
      fields[consentType.code] = [{ value: null, disabled: true }];
    });

    this.consentFormGroup = this.fb.group(fields);

    this.consentFormGroup.get('type').valueChanges.subscribe({
      next: (consentType: Dictionary) =>
      {
        const expirationDateValidator = [];
        if (!consentType || consentType.code === 'exclusion')
        {
          this.consentFormGroup.get('expirationDate').disable();
        }
        else
        {
          expirationDateValidator.push(Validators.required);
          this.consentFormGroup.get('expirationDate').enable();
        }
        this.consentFormGroup.get('expirationDate').setValidators(expirationDateValidator);
        this.consentFormGroup.get('expirationDate').updateValueAndValidity();
      }
    });

    this.consentFormGroup.get('signedBy').valueChanges.subscribe({
      next: (signedBy: Dictionary) =>
      {
        const authorizedByValidator = [];
        if (!signedBy || signedBy.code === 'self')
        {
          this.consentFormGroup.get('authorizedBy').setValue(this.patientName);
          this.consentFormGroup.get('authorizedBy').disable();
        }
        else
        {
          authorizedByValidator.push(Validators.required);
          this.consentFormGroup.get('authorizedBy').enable();
        }
        this.consentFormGroup.get('authorizedBy').setValidators(authorizedByValidator);
        this.consentFormGroup.get('authorizedBy').updateValueAndValidity();
      }
    });

    event.sender.addRow(this.consentFormGroup);

    // Call organization change to set consent state options
    if (defaultOrganization !== undefined)
    {
      this.organizationChange();
    }

    this.consentFormGroup.get('type').markAsTouched();
    this.consentFormGroup.get('signedBy').markAsTouched();
    this.consentFormGroup.get('authorizedBy').markAsTouched();
    this.consentFormGroup.get('organization').markAsTouched();
    this.consentFormGroup.get('signedDate').markAsTouched();
    this.consentFormGroup.get('expirationDate').markAsTouched();
    this.consentFormGroup.markAsDirty();
  }

  /**
   * Consent signed date changed.
   */
  public signedDateChange(): void
  {
    this.setExpirationDateDefault();
  }

  /**
   * Disable Consent Type
   *
   * @param itemArgs Item object
   */
  public consentTypeItemDisabled(itemArgs: { dataItem: Dictionary; index: number }): boolean
  {
    return itemArgs.dataItem.code === 'community';
  }

  public consentTypeChange(): void
  {
    const currentConsentType = this.consentFormGroup.get('type').value;
    if (currentConsentType !== null)
    {
      let availableConsents = [];
      if (this.consentFormGroup.get('organization').value &&
        this.consentFormGroup.get('organization').value.consentCollects)
      {
        availableConsents = this.consentFormGroup.get('organization').value.consentCollects;
      }

      this.consentableDataTypes.forEach((consentType: Dictionary) =>
      {
        const foundConsent = availableConsents.findIndex(x => x.code === consentType.code);
        if (this.consentableOptionsDisabledReadOnlyOnInsert.indexOf(currentConsentType.id) == -1)
        {
          if (foundConsent > -1)
          {
            this.consentFormGroup.get(consentType.code).enable();
          }
          else
          {
            this.consentFormGroup.get(consentType.code).disable();
            this.consentFormGroup.get(consentType.code).setValue(null);
          }
        }
        else
        {
          if (foundConsent > -1)
          {
            this.consentFormGroup.get(consentType.code).disable();
            this.consentFormGroup.get(consentType.code).setValue(false);
          }
          else
          {
            this.consentFormGroup.get(consentType.code).disable();
            this.consentFormGroup.get(consentType.code).setValue(null);
          }
        }
        this.consentFormGroup.get(consentType.code).updateValueAndValidity();
      });
    }
    this.setExpirationDateDefault();
  }

  /**
   * Consent organization changed.
   */
  public organizationChange(): void
  {
    this.setExpirationDateDefault();

    // Enable or disable consent controls
    this.consentTypeChange();
  }

  /**
   * Cancelling the Consent add action.
   *
   * @param event Current row
   */
  public consentCancelHandler(event: GridEventHandler): void
  {
    this.editing = false;
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Delete entered Consent from grid.
   *
   * @param consent Consent record to remove.
   */
  public deleteConsent(consent: any): void
  {
    if (this.isPendingConsent(consent.id))
    {
      this.pendingConsents = this.pendingConsents.filter(c => c.id !== consent.id);
      this.add.emit(this.pendingConsents);
    }
    else
    {
      let found = false;
      this.deletedConsents.forEach((id) => { if (id === parseInt(consent.id, 10)) { found = true; } });
      if (!found)
      {
        this.deletedConsents.push(consent.id);
      }
      this.delete.emit(this.deletedConsents);
    }
    this.gridView.data = this.gridView.data.filter(c => c.id !== consent.id);
  }

  /**
   * Consent save action.
   *
   * @param event Current record
   */
  public consentSaveHandler(event: GridEventHandler): void
  {
    // Do not validate Consent options if exclusion
    if (this.consentFormGroup.get('type').value.code !== 'exclusion') 
    {
      // Must at least set one consent value to checked or unchecked
      let selectedConsents = 0;
      this.consentableDataTypes.forEach((consentType: Dictionary) =>
      {
        if (this.consentFormGroup.get(consentType.code).value !== null)
        {
          selectedConsents += 1;
        }
      });

      // If no consent set, show error and stop.
      if (selectedConsents === 0)
      {
        this.messageHandlerService.displayError('NO-CONSENT-SET');
        return;
      }
    }

    this.consentFormGroup.get('editing').setValue(false);

    // Reset expiration date if exclusion
    if (this.consentFormGroup.get('type').value.code === 'exclusion') 
    {
      this.consentFormGroup.get('expirationDate').setValue(null);
    }

    // Construct data
    const data: any = {};
    data.id = this.consentFormGroup.get('id').value;
    data.organization = this.consentFormGroup.get('organization').value;
    data.signedDate = this.consentFormGroup.get('signedDate').value;
    data.expirationDate = this.consentFormGroup.get('expirationDate').value;
    data.type = this.consentFormGroup.get('type').value;
    data.signedBy = this.consentFormGroup.get('signedBy').value;
    data.authorizedBy = this.consentFormGroup.get('authorizedBy').value;
    data.items = [];

    if (this.consentFormGroup.get('type').value.code !== 'exclusion') 
    {
      this.consentableDataTypes.forEach((consentType: Dictionary) =>
      {
        data.items.push({
          consentValue: this.consentFormGroup.get(consentType.code).value,
          dictionary: this.consentableDataTypes.find(x => x.code === consentType.code)
        });
      });
    }

    this.pendingConsents.push(data);
    this.gridView.data.unshift(data);

    event.sender.closeRow(event.rowIndex);
    this.add.emit(this.pendingConsents);
    this.editing = false;
  }

  /**
   * Get Consent object for the field.
   *
   * @param items List of consent items.
   * @param field Current field.
   * @returns Consent object.
   */
  public getConsentValue(dataItem: any, field: string): any
  {
    const consentObject = dataItem.items.find((x: any) => x.dictionary.code === field);
    return (consentObject !== undefined) ? consentObject.consentValue : null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.setStateFilters();
    this.memberConsentListService.queryByState(state);
  }

  /**
   * Check if the entry is a pending consent. A pending consent is an entry added but not saved yet. In that case, the id is a guid.
   *
   * @param id Id for the current row.
   * @returns True is the id is a pending consent and contains a -
   */
  public isPendingConsent(id: any): boolean
  {
    return id !== null && id !== undefined ? id.toString().indexOf('-') > -1 : false;
  }

  /**
   * Check if we show the consent details. We currently show details only if the Consent is persisted.
   *
   * @param dataItem Data item
   * @returns True if Consent is persisted.
   */
  public showConsentDetails = (dataItem: any) => !this.isPendingConsent(dataItem.id) && !(dataItem?.type?.code === 'verbal' || dataItem?.type?.code === 'verbal-consent-and-auth');

  /**
   * Document either added or remove and send a Summary Refresh Count message.
   */
  public documentModified(): void
  {
    this.messengerService.sendMessage(new MessageSubject('refreshSummaryCounts'));
  }

  public revokeAllAction(confirm: boolean): void
  {
    this.close('revokeAll');
    if (confirm)
    {
      this.memberService.revokeAllConsents(this.currentMember.id).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('CONSENT-REVOKED-SUCCESSFULLY');
          this.state.skip = 0;
          this.memberConsentListService.queryByState(this.state);

          this.messengerService.sendMessage(new MessageSubject('refreshMemberConsent'));
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Check if the revoke all button is visible.
   *
   * @returns Returns true if the revoke all button make sense.
   */
  public revokeAllVisible(): boolean
  {
    return this.currentMember && this.currentMember.id > 0 &&                       // Must have a member persisted
      this.total > 0 && !this.editing &&                                            // Must have consent and not in editing mode
      this.pendingConsents.length === 0 &&                                          // Must have no pending consents
      this.deletedConsents.length === 0 &&                                          // Must have no deleted consents
      this.gridView.data.filter(c => c.id !== null && c.id > 0 &&                   // Must be persisted ids
        c.revokedDate === null && c.consentExpiration &&                            // Must not already revoked/expired (will contains a -)
        !c.consentExpiration.includes('-')).length > 0;
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Selected cases.
   */
  public onSelectedKeysChange(e: any): void
  {
    this.selectedCaseKeys = e;
    if (this.selectedCaseKeys.length === 0)
    {
      this.selectAllState = 'unchecked';
    }
    else if (this.selectedCaseKeys.length > 0 &&
      this.selectedCaseKeys.length < this.filterRevokableConsents().length)
    {
      this.selectAllState = 'indeterminate';
    }
    else
    {
      this.selectAllState = 'checked';
    }
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState)
  {
    if (checkedState === 'checked')
    {
      this.selectedCaseKeys = this.filterRevokableConsents().map((item) => item.id);
      this.selectAllState = 'checked';
    }
    else
    {
      this.clearSelectedConsents();
    }
  }

  public canRevokeConsent(dataItem: any): boolean
  {
    return !dataItem.editing &&
      (dataItem.revokedDate === null) &&
      (dataItem.consentExpiration === undefined || dataItem.consentExpiration === null || dataItem.consentExpiration >= 0) &&
      (!this.isPendingConsent(dataItem.id));
  }

  public filterRevokableConsents(): any
  {
    return this.gridView.data.filter((dataItem) => this.canRevokeConsent(dataItem));
  }

  /**
   * Provides the cases selected count. This controls the 'Select' button state and count display.
   *
   * @returns Number Total count of the selected providers.
   */
  public getSelectedCount(): number
  {
    return this.selectedCaseKeys.length;
  }

  public openRevokeConsentsDialogOpened(): void
  {
    this.revokeConsentsDialogOpened = true;
  }

  public cancelRevokeConsents(): void
  {
    this.revokeConsentsDialogOpened = false;
  }

  public revokeConsents(): void
  {
    this.memberService.revokeSelectedConsents(this.selectedCaseKeys).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CONSENTS-REVOKED-SUCCESSFULLY');
        this.clearSelectedConsents();
        this.state.skip = 0;
        this.memberConsentListService.queryByState(this.state);
        this.messengerService.sendMessage(new MessageSubject('refreshMemberConsent'));
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.revokeConsentsDialogOpened = false;
      },
      complete: () =>
      {
        this.revokeConsentsDialogOpened = false;
      }
    });
  }

  public getDetailsLink(): void
  {
    const gridElement = this.el.nativeElement.querySelector('kendo-grid');

    if (gridElement)
    {
      const expandCells: NodeListOf<HTMLElement> = gridElement.querySelectorAll('.k-grid td:first-child');

      expandCells.forEach(cell =>
      {
        if (cell.querySelector('a') != null)
        {
          this.link.push(cell.querySelector('a')); // seek link element and convert to button
        }
        if (this.link.length)
        {
          this.detailsUpdated = true;
        }
      });
    }
  }

  public updateDetailsModal(): void
  {
    if (!this.link)
    {
      return;
    }

    this.link.forEach((value: any) =>
    {
      this.renderer.setAttribute(value, 'role', 'button');
      const isExpanded = value.getAttribute('aria-label').includes("Collapse Details");

      this.renderer.setAttribute(value, 'aria-expanded', isExpanded ? 'true' : 'false');
    });
  }

  /**
   * Set Consent Expiration Months when changing the organization or the signed date.
   */
  private setExpirationDateDefault(): void
  {
    if (this.consentFormGroup && this.consentFormGroup.get('organization').value &&
      this.consentFormGroup.get('signedDate').value)
    {
      // Use number of months from organization if set, otherwise use system settings.
      let months = null;
      let days = null;
      if (this.consentFormGroup.get('organization').value.consentExpirationMonths)
      {
        months = this.consentFormGroup.get('organization').value.consentExpirationMonths;
      }
      else
      {
        const consentType = this.consentFormGroup.get('type').value.code;
        const consents = this.consentExpiration.split(',');
        let consent = consents.find(p => p.startsWith(consentType + '|'));
        if (consent === null || consent === undefined)
        {
          // get default value
          consent = consents.find(p => p.startsWith(this.translate.instant('DEFAULT').charAt(0) + '|'));
        }
        consent = consent.split('|');
        if (consent[2] === this.translate.instant('CONSENT-EXPIRATION-POLICY-MONTHS').charAt(0))
        {
          months = consent[1]; // Consent Expiration Policy in Months
        }
        else
          if (consent[2] === this.translate.instant('CONSENT-EXPIRATION-POLICY-DAYS').charAt(0))
          {
            days = consent[1]; // Consent Expiration Policy in Days
          }
      }
      if (months !== null && months > 0)
      {
        const newExpirationDate = moment(this.consentFormGroup.get('signedDate').value)
          .add(months, 'months').toDate();
        this.consentFormGroup.get('expirationDate').setValue(newExpirationDate);
      }
      else
        if (days !== null && days > 0)
        {
          const newExpirationDate = moment(this.consentFormGroup.get('signedDate').value)
            .add(days, 'days').toDate();
          this.consentFormGroup.get('expirationDate').setValue(newExpirationDate);
        }
    }
  }

  /**
   * Initialize Consent grid columns (fixed and consentable properties)
   *
   * @param res Resources list.
   */
  private initGridColumns(res: any): void
  {
    const consentModelKey = this.consentModel === 'ORGANIZATIONAL' ? 'CONSENTED-ORGANIZATION' : 'SOURCE-ORGANIZATION';
    this.columnsConfig = [
      {
        field: 'type', title: res.TYPE, titleKeys: ['TYPE'],
        width: 80, sortable: true, hidden: false, filterable: false, dataType: 'string'
      },
      {
        field: 'organizationName', title: res[consentModelKey], titleKeys: [consentModelKey],
        width: 120, sortable: true, hidden: false, filterable: false, dataType: 'string'
      },
      {
        field: 'signedBy', title: res['SIGNED-BY'], titleKeys: ['SIGNED-BY'],
        width: 120, sortable: true, hidden: false, filterable: false, dataType: 'string'
      },
      {
        field: 'authorizedBy', title: res['AUTHORIZED-BY'], titleKeys: ['AUTHORIZED-BY'],
        width: 120, sortable: true, hidden: false, filterable: false, dataType: 'string'
      },
      {
        field: 'signedDate', title: res['CONSENT-DATE-SIGNED'],
        titleKeys: ['CONSENT-DATE-SIGNED'], width: 90, sortable: true, hidden: false, filterable: false,
        format: '{0:d}', filter: 'date', dataType: 'date'
      },
      {
        field: 'expirationDate', title: res['CONSENT-EXPIRATION-DATE'], titleKeys: ['CONSENT-EXPIRATION-DATE'],
        width: 90, sortable: true, hidden: false, filterable: false, format: '{0:d}', filter: 'date', dataType: 'date'
      },
      {
        field: 'consentExpiration', title: res['CONSENT-EXPIRATION'], titleKeys: ['CONSENT-EXPIRATION'],
        width: 80, sortable: true, hidden: false, filterable: false, dataType: 'string'
      },
      {
        field: 'memberConsentStatus', title: res.STATUS, titleKeys: ['STATUS'],
        width: 80, sortable: true, hidden: true, filterable: false, dataType: 'string'
      },
    ];

    // Add column for each consentable data types
    this.consentableDataTypes.forEach((consentType: Dictionary) =>
    {
      this.columnsConfig.push({
        field: consentType.code, title: consentType.displayName, titleKeys: null,
        width: 35, sortable: false, hidden: false, filterable: false, class: 'ellipsis-text text-center', dataType: 'boolean'
      });
    });
  }

  /**
   * Clear grid results
   */
  private clearGrid(): void
  {
    this.gridView = { data: [], total: 0 };
  }

  private loadMemberConsents(): void
  {
    this.clearGrid();
    this.state.skip = 0;
    this.state.sort = [{ field: 'signedDate', dir: 'desc' }];
    this.setStateFilters();
  }

  private setStateFilters(): void
  {
    this.state.filter.filters = [];
    this.state.filter.filters.push({ field: 'memberId', operator: 'eq', value: this.currentMember && this.currentMember.id > 0 ? this.currentMember.id : 0 });
    // Exclude marked for deletion consents
    this.deletedConsents.forEach((id) => { this.state.filter.filters.push({ field: 'id', operator: 'neq', value: id }); });
    this.memberConsentListService.queryByState(this.state);
  }

  private consentExpirationColor(numberOfDays: number): string
  {
    const start = moment();
    const end = moment().add(numberOfDays, 'days');
    const months = end.diff(start, 'months');
    if (months > 6)
    {
      return this.memberService.memberConsentGreen;
    }
    else if (months < 3)
    {
      return this.memberService.memberConsentRed;
    }
    else
    {
      return this.memberService.memberConsentOrange;
    }
  }

  private clearSelectedConsents(): void
  {
    this.selectedCaseKeys = [];
    this.selectAllState = 'unchecked';
  }
}
